import React from "react";
import { AiOutlineUser, AiOutlineCalendar, AiOutlineClockCircle } from "react-icons/ai";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { BsTelephone, BsEnvelope, BsGenderAmbiguous } from "react-icons/bs";
import { FaRegBuilding, FaHeart, FaGlobeAfrica, FaMoon, FaLeaf } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import GoogleInput from "../../../../common/GoogleInput";
import ErrorMessage from "../../../../common/ErrorMessage";

interface Props {
  register: any;
  errors: any;
  handleChangePhone: (phone: any, country: any) => void;
  phone: string;
  onChangeAdresse: (val: any) => void;
  adresse: any;
  setValue: any;
  watch: any;
}

const situationsMatrimoniales = ["Célibataire", "Marié(e)", "Divorcé(e)", "Veuf(ve)"];
const nationalites = ["Sénégalaise", "Malienne", "Ivoirienne", "Autre"];
const religions = ["Musulmane", "Chrétienne", "Autre"];

// Champ "boîte" avec icône + label au-dessus + valeur/placeholder en dessous,
// pour matcher exactement la maquette (champs arrondis type carte).
function FieldBox({
  icon,
  label,
  children,
  error,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <div className="pi-field-box">
      <span className="pi-field-icon">{icon}</span>
      <div className="pi-field-content">
        <span className="pi-field-label">{label}</span>
        {children}
      </div>
      {error && <ErrorMessage message={error} />}
    </div>
  );
}

function InformationsPersonnelles({
  register,
  errors,
  handleChangePhone,
  phone,
  onChangeAdresse,
  adresse,
  setValue,
  watch,
}: Props) {
  const sexe = watch ? watch("sexe") : undefined;

  return (
    <div className="pi-card">
      <h2 className="pi-title">Vos informations personnelles</h2>
      <p className="pi-subtitle">
        Veuillez renseigner vos informations de contact et d'adresse.
      </p>

      <div className="pi-banner">
        <span className="pi-banner-check">✓</span>
        <span>
          Vos données sont confidentielles et utilisées uniquement pour le
          traitement de votre consultation.
        </span>
      </div>

      <FieldBox
        icon={<AiOutlineUser />}
        label="Nom complet"
        error={errors?.nom_complet?.message}
      >
        <input
          className="pi-input"
          type="text"
          placeholder="Adjaratou Khady NDIAYE"
          {...register("nom_complet")}
        />
      </FieldBox>

      <div className="pi-row">
        <FieldBox icon={<BsTelephone />} label="Téléphone" error={errors?.telephone?.message}>
          <PhoneInput
            inputProps={{ name: "phone" }}
            country="sn"
            value={phone}
            enableSearch={true}
            onChange={(phone, country) => handleChangePhone(phone, country)}
            inputClass="pi-phone-input"
            containerClass="pi-phone-container"
            buttonClass="pi-phone-button"
            enableAreaCodes={true}
            prefix="+"
            countryCodeEditable={false}
          />
        </FieldBox>
        <FieldBox icon={<BsEnvelope />} label="E-mail" error={errors?.email?.message}>
          <input
            className="pi-input"
            type="email"
            placeholder="Ex : email@exemple.com"
            {...register("email")}
          />
        </FieldBox>
      </div>

      <FieldBox
        icon={<HiOutlineLocationMarker />}
        label="Adresse"
        error={errors?.adresse?.message}
      >
        <GoogleInput
          className="pi-input"
          value={adresse}
          onChange={onChangeAdresse}
          placeholder="Ouest Foire"
        />
      </FieldBox>

      <div className="pi-row pi-row-3">
        <FieldBox icon={<AiOutlineCalendar />} label="Âge">
          <input className="pi-input" type="number" placeholder="Ex : 30" {...register("age")} />
        </FieldBox>
        <FieldBox icon={<FaRegBuilding />} label="Profession">
          <input
            className="pi-input"
            type="text"
            placeholder="Ex : Commerçante"
            {...register("profession")}
          />
        </FieldBox>
        <FieldBox icon={<FaHeart className="me-2" />} label="Situation matrimoniale">
          <select className="pi-input pi-select" {...register("situation_matrimoniale")} defaultValue="">
            <option value="" disabled>
              Sélectionner
            </option>
            {situationsMatrimoniales.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FieldBox>
      </div>

      <p className="pi-sexe-label">Sexe</p>
      <div className="pi-sexe-row">
        <button
          type="button"
          className={`pi-sexe-btn ${sexe === "Masculin" ? "active" : ""}`}
          onClick={() => setValue("sexe", "Masculin")}
        >
          <BsGenderAmbiguous /> Masculin
        </button>
        <button
          type="button"
          className={`pi-sexe-btn ${sexe === "Feminin" ? "active" : ""}`}
          onClick={() => setValue("sexe", "Feminin")}
        >
          <BsGenderAmbiguous /> Féminin
        </button>
      </div>
      <input type="hidden" {...register("sexe")} />

      <div className="pi-row">
        <FieldBox icon={<FaGlobeAfrica />} label="Nationalité">
          <select className="pi-input pi-select" {...register("nationalite")} defaultValue="">
            <option value="" disabled>
              Sélectionner
            </option>
            {nationalites.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </FieldBox>
        <FieldBox icon={<FaMoon />} label="Religion">
          <select className="pi-input pi-select" {...register("religion")} defaultValue="">
            <option value="" disabled>
              Sélectionner
            </option>
            {religions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </FieldBox>
      </div>

      <div className="pi-row">
        <FieldBox
          icon={<AiOutlineCalendar />}
          label="Prendre le rendez-vous"
          error={errors?.date_rendez_vous?.message}
        >
          <input
            className="pi-input"
            type="date"
            placeholder="jj/mm/aaaa"
            {...register("date_rendez_vous")}
          />
        </FieldBox>
        <FieldBox
          icon={<AiOutlineClockCircle />}
          label="Heure du rendez-vous"
          error={errors?.heure_rendez_vous?.message}
        >
          <input className="pi-input" type="time" {...register("heure_rendez_vous")} />
        </FieldBox>
      </div>

      <FieldBox icon={<FaLeaf />} label="Autres traitements">
        <input
          className="pi-input"
          type="text"
          placeholder="Ex : Massage, conseils nutritionnels..."
          {...register("autres_traitements")}
        />
      </FieldBox>
    </div>
  );
}

export default InformationsPersonnelles;